import { useCallback, useRef, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import { ModalShell, useToast } from '@busy/ui';
import { MAX_VIDEO_CAPTURE_SEC, MIN_VIDEO_CAPTURE_SEC } from '@busy/core';
import { useT } from '../lib/i18n';
import { dismissFramingTip, isFramingTipDismissed } from '../lib/framingTip';
import type { ComposeRole, PhotoCropArea } from './PhotoCaptureModal';

// Quick-clip transformation video capture. Mirrors PhotoCaptureModal's
// pattern: invokes the device's NATIVE camera app via a file input with
// `capture="environment"`. iOS handles orientation, audio, focus, exposure
// — all the things we got wrong rolling our own getUserMedia + canvas
// pipeline. The video Blob comes back already correctly oriented for
// playback / share / stitch.
//
// Path 1 ("ephemeral video") architecture: nothing the user records ever
// touches Supabase Storage. The video Blob lives in the cut-timer store
// in memory and dies when the modal flow ends. Sharing happens later on
// the post-cut Transformation Reveal screen — never here.

const POSTER_TYPE = 'image/jpeg';
// 2048 + 0.94 quality matches PhotoCaptureModal — the still extracted
// from the last frame of a video gets the same fidelity treatment as
// a freshly-captured photo, since it lands in the same downstream
// pipelines (client gallery, share composite).
const POSTER_QUALITY = 0.94;
const POSTER_SIZE = 2048;

export function VideoCaptureModal({
  open,
  title,
  composeRole,
  onCapture,
  onClose,
}: {
  open: boolean;
  /** Headline copy — e.g. "Quick before clip" or "After-cut reveal". */
  title: string;
  /** Optional. Which half of the eventual transformation composite this
   *  video will end up in. When set we (a) show a composition heads-up
   *  on the capture stage so the barber knows what to frame for before
   *  handing off to the OS camera, and (b) overlay the picture frame
   *  on the playback preview so they can decide retake-vs-save against
   *  the actual final layout. Unset for non-transformation captures. */
  composeRole?: ComposeRole;
  /** Called with the recorded video Blob + a still-frame JPEG when the
   *  barber confirms. The still is what the cut timer stores as the
   *  client photo; the video Blob is what the reveal step uses to compose
   *  the transformation. `videoCrop` is the optional reposition crop
   *  rect (in native video pixel coords) — passed when the barber pans/
   *  zooms on the crop step. videoStitch.ts uses it as the source rect
   *  when drawing each frame of the video. */
  onCapture: (out: { photoBlob: Blob; videoBlob: Blob; videoCrop: PhotoCropArea | null }) => Promise<void> | void;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [videoBlob, setVideoBlob] = useState<Blob | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Shared framing-tip dismissal — same localStorage flag as the photo
  // modal so dismissing once kills the tip across both flows.
  const [tipDismissed, setTipDismissed] = useState(() => isFramingTipDismissed());
  // Crop step state. The crop is "live" — the video plays inside the
  // Cropper element, pan/zoom drag the playback's framing in real time
  // so the barber sees actual motion while picking the composition.
  // Only used when composeRole is set (transformation captures);
  // composeRole-less callers just save the raw clip without a crop.
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  function reset() {
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(null);
    setVideoUrl(null);
    setVideoDuration(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }

  function handleClose() {
    reset();
    onClose();
  }

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !file.type.startsWith('video/')) return;
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoBlob(file);
    setVideoUrl(URL.createObjectURL(file));
  }

  async function handleSave() {
    if (!videoBlob) return;
    setBusy(true);
    const photo = await extractLastFrame(videoBlob);
    if (!photo) {
      toast(t('videoCapture.frameError'), 'error');
      setBusy(false);
      return;
    }
    // Crop only flows through when composeRole was set AND the barber
    // got the crop UI; transformation-less captures save raw clips.
    const videoCrop: PhotoCropArea | null =
      composeRole && croppedAreaPixels
        ? {
            x: croppedAreaPixels.x,
            y: croppedAreaPixels.y,
            width: croppedAreaPixels.width,
            height: croppedAreaPixels.height,
          }
        : null;
    try {
      await onCapture({ photoBlob: photo, videoBlob, videoCrop });
      reset();
    } finally {
      setBusy(false);
    }
  }

  if (!open) return null;
  return (
    <ModalShell open onClose={handleClose} variant="card" size="md" ariaLabel={title}>
      <div className="bg-surface rounded-2xl">
        <div className="px-5 pt-5 pb-2">
          <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500 mb-1">
            {t('videoCapture.eyebrow')}
          </div>
          <h2 className="font-display text-2xl leading-tight">{title}</h2>
          <p className="text-[12px] text-ink-500 mt-1.5 leading-snug">
            {videoUrl ? t('videoCapture.reviewHint') : t('videoCapture.captureHint')}
          </p>
        </div>

        {videoUrl ? (
          <>
            {composeRole ? (
              <>
                {/* Crop step — only runs for transformation captures.
                    react-easy-crop with the video= prop drives a live
                    playback inside the cropper; pan + zoom drag the
                    framing in real time so the barber sees motion
                    (clipper passes, head movement) while picking the
                    composition. The frame overlay sits ON TOP of the
                    cropper at 75% opacity so the BEFORE/AFTER banners
                    show exactly where chrome will hide the recording
                    in the final stitched output.
                    aspect=1 matches what videoStitch.ts expects (a
                    square crop fed into the half-strip via cover-fit). */}
                <div className="relative bg-bg-alt mx-5 my-3 rounded-xl overflow-hidden" style={{ height: 360 }}>
                  <Cropper
                    video={videoUrl}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                    onMediaLoaded={({ naturalHeight, naturalWidth }) => {
                      // Cropper fires this before the video's own loaded-
                      // metadata in some browsers — but we need duration
                      // for the min/max gates, so fall back to a hidden
                      // probe if the cropper didn't surface it. Cropper
                      // passes the intrinsic dimensions; duration lives
                      // on the underlying <video> element which is not
                      // exposed by react-easy-crop. We probe duration
                      // separately on the source URL.
                      void naturalHeight;
                      void naturalWidth;
                    }}
                    showGrid={false}
                    cropShape="rect"
                    objectFit="cover"
                    mediaProps={{ autoPlay: true, loop: true, muted: true, playsInline: true }}
                    style={{ containerStyle: { background: '#0c1530' } }}
                  />
                  {/* Frame chrome overlay, same as the photo crop step.
                      pointer-events-none so the cropper underneath
                      still receives all drag + pinch gestures. */}
                  <div
                    aria-hidden
                    className="absolute inset-0 overflow-hidden pointer-events-none"
                  >
                    <img
                      src="/assets/frame-portrait.png"
                      alt=""
                      className="absolute left-0 w-full"
                      style={{
                        [composeRole === 'before' ? 'top' : 'bottom']: 0,
                        opacity: 0.75,
                      }}
                    />
                    <div className="absolute top-0 left-0 right-0 bg-black/55 text-white text-[8px] tracking-[0.18em] uppercase font-extrabold flex items-center justify-center" style={{ height: '5.6%' }}>
                      <span>edge trim</span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/55 text-white text-[8px] tracking-[0.18em] uppercase font-extrabold flex items-center justify-center" style={{ height: '5.6%' }}>
                      <span>edge trim</span>
                    </div>
                  </div>
                </div>

                {/* Zoom slider — mirrors the photo crop step. Touch
                    pinch on the cropper above also drives zoom. */}
                <div className="px-5 pb-2 flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-wide font-bold text-ink-500">
                    {t('videoCapture.zoom')}
                  </span>
                  <input
                    type="range"
                    min={1}
                    max={4}
                    step={0.05}
                    value={zoom}
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className="flex-1 accent-gold-500"
                    aria-label={t('videoCapture.zoom')}
                  />
                </div>

                {/* Hidden probe for duration — Cropper doesn't expose
                    the <video> element's duration via its onMediaLoaded
                    callback, so we re-load the same source here just to
                    read the duration off it and gate Save on min/max.
                    Cheap (browser dedupes the URL fetch). */}
                <video
                  src={videoUrl}
                  preload="metadata"
                  muted
                  playsInline
                  className="hidden"
                  onLoadedMetadata={(e) => {
                    const d = e.currentTarget.duration;
                    if (Number.isFinite(d) && d > 0) setVideoDuration(d);
                  }}
                />
              </>
            ) : (
              // Plain playback preview — used by non-transformation
              // callers (rare today, kept for future general-purpose
              // video capture). No crop, no frame overlay.
              <div className="relative bg-black mx-5 my-3 rounded-xl overflow-hidden max-h-[55dvh]">
                <video
                  src={videoUrl}
                  controls
                  autoPlay
                  loop
                  playsInline
                  onLoadedMetadata={(e) => {
                    const d = e.currentTarget.duration;
                    if (Number.isFinite(d) && d > 0) setVideoDuration(d);
                  }}
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            {videoDuration !== null && videoDuration < MIN_VIDEO_CAPTURE_SEC && (
              <div className="mx-5 mb-2 rounded-xl border border-amber-500/40 bg-amber-50 dark:bg-amber-600/10 p-3 text-[12px] text-amber-900 dark:text-amber-200 leading-snug">
                {t('videoCapture.tooShort', {
                  min: MIN_VIDEO_CAPTURE_SEC,
                  actual: videoDuration.toFixed(1),
                })}
              </div>
            )}
            {videoDuration !== null && videoDuration > MAX_VIDEO_CAPTURE_SEC && (
              <div className="mx-5 mb-2 rounded-xl border border-amber-500/40 bg-amber-50 dark:bg-amber-600/10 p-3 text-[12px] text-amber-900 dark:text-amber-200 leading-snug">
                {t('videoCapture.tooLong', {
                  max: MAX_VIDEO_CAPTURE_SEC,
                  actual: videoDuration.toFixed(1),
                })}
              </div>
            )}

            <div className="p-4 flex gap-2">
              <button
                type="button"
                onClick={reset}
                disabled={busy}
                className="h-11 px-4 rounded-lg border border-line bg-bg-alt text-ink-700 font-semibold disabled:opacity-50"
              >
                {t('videoCapture.retake')}
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={
                  busy ||
                  videoDuration === null ||
                  videoDuration < MIN_VIDEO_CAPTURE_SEC ||
                  videoDuration > MAX_VIDEO_CAPTURE_SEC
                }
                className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold disabled:opacity-50"
              >
                {busy ? t('videoCapture.saving') : t('videoCapture.save')}
              </button>
            </div>

            <p className="px-4 pb-4 text-[11px] text-ink-500 leading-snug">
              {t('videoCapture.afterSaveHint')}
            </p>
          </>
        ) : (
          <div className="p-5 space-y-3">
            {/* Pre-capture composition heads-up — only for transformation
                captures. Mirrors the photo flow's hint so the barber
                gets the same "frame your head clear of the banners"
                guidance before opening the OS camera. The actual fit
                check happens post-recording when we overlay the frame
                on the playback preview. */}
            {composeRole && !tipDismissed && (
              <div className="rounded-xl border border-line bg-bg-alt p-3 flex items-start gap-3 relative">
                <div className="relative w-14 shrink-0 overflow-hidden rounded-md bg-black/40" style={{ aspectRatio: '9 / 16' }}>
                  <img
                    src="/assets/frame-portrait.png"
                    alt=""
                    aria-hidden
                    className="absolute inset-0 w-full h-full object-cover opacity-80"
                  />
                </div>
                <div className="min-w-0 flex-1 pr-5">
                  <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-gold-500">
                    {t('videoCapture.composeEyebrow')}
                  </div>
                  <p className="text-[12px] leading-snug text-ink-700 mt-0.5">
                    {composeRole === 'before'
                      ? t('videoCapture.composeHintBefore')
                      : t('videoCapture.composeHintAfter')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    dismissFramingTip();
                    setTipDismissed(true);
                  }}
                  aria-label={t('videoCapture.composeDismissAria')}
                  className="absolute top-1.5 right-1.5 w-7 h-7 grid place-items-center rounded-full text-ink-500 hover:text-ink-900 hover:bg-black/5"
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </svg>
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full h-32 rounded-2xl border-2 border-dashed border-line-strong bg-bg-alt text-ink-700 grid place-items-center hover:border-navy-900 dark:hover:border-gold-500 transition-colors"
            >
              <div className="flex flex-col items-center gap-1.5">
                <FilmIcon className="w-8 h-8 text-navy-900 dark:text-gold-400" />
                <span className="text-sm font-bold">{t('videoCapture.openCamera')}</span>
                <span className="text-[11px] text-ink-500">{t('videoCapture.openCameraHint')}</span>
              </div>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              capture="environment"
              onChange={handleFile}
              className="hidden"
            />
            <button
              type="button"
              onClick={handleClose}
              className="w-full h-11 rounded-lg border border-line bg-surface text-ink-700 font-semibold"
            >
              {t('videoCapture.cancel')}
            </button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// Pull a still frame from the END of the recorded clip for use as the
// client photo. Loads the blob into a hidden <video>, seeks to the last
// frame, draws to a square canvas (cover-fit). Same approach as
// PhotoCaptureModal's renderSquareCrop, just sourced from a video.
async function extractLastFrame(blob: Blob): Promise<Blob | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    const cleanup = () => URL.revokeObjectURL(url);
    const fail = () => {
      cleanup();
      resolve(null);
    };

    video.addEventListener('loadedmetadata', () => {
      const target = Math.max(0, video.duration - 0.05);
      const onSeeked = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = POSTER_SIZE;
          canvas.height = POSTER_SIZE;
          const ctx = canvas.getContext('2d');
          if (!ctx) return fail();
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          const scale = Math.max(POSTER_SIZE / vw, POSTER_SIZE / vh);
          const drawW = vw * scale;
          const drawH = vh * scale;
          const dx = (POSTER_SIZE - drawW) / 2;
          const dy = (POSTER_SIZE - drawH) / 2;
          ctx.fillStyle = '#000';
          ctx.fillRect(0, 0, POSTER_SIZE, POSTER_SIZE);
          ctx.drawImage(video, dx, dy, drawW, drawH);
          canvas.toBlob(
            (b) => {
              cleanup();
              resolve(b);
            },
            POSTER_TYPE,
            POSTER_QUALITY,
          );
        } catch {
          fail();
        }
      };
      video.addEventListener('seeked', onSeeked, { once: true });
      video.currentTime = target;
    });
    video.addEventListener('error', fail, { once: true });
  });
}

function FilmIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18" />
      <line x1="7" y1="2" x2="7" y2="22" />
      <line x1="17" y1="2" x2="17" y2="22" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <line x1="2" y1="7" x2="7" y2="7" />
      <line x1="2" y1="17" x2="7" y2="17" />
      <line x1="17" y1="17" x2="22" y2="17" />
      <line x1="17" y1="7" x2="22" y2="7" />
    </svg>
  );
}
